import { ParkingtonTool, isBrushTool, isDragTool, type ParkingtonState } from '../ParkingtonTypes';
import { House } from '@antification/core';
import type { InputCallbacks } from './InputTypes';

/**
 * Handles mouse move/down/up events for the Parkington layer.
 */
export class MouseHandler {
  handleMouseMove(e: MouseEvent, state: ParkingtonState, callbacks: InputCallbacks): void {
    if (state.inputBlocked) return;
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    callbacks.setMousePosition(mouseX, mouseY);

    // Check if mouse moved significantly
    const moved = Math.abs(mouseX - state.mouseX) > 3 ||
                  Math.abs(mouseY - state.mouseY) > 3;

    if (moved) {
      callbacks.setLastMousePosition(mouseX, mouseY);
      callbacks.resetMouseIdleTimer();
    }

    // Update hovered cell
    callbacks.setHoveredCell(callbacks.screenToCell(mouseX, mouseY));

    // Update hovered entity when SELECT tool is active
    const hoveredCell = callbacks.screenToCell(mouseX, mouseY);
    if (state.currentTool === ParkingtonTool.SELECT && hoveredCell) {
      callbacks.updateHoveredEntity();
    } else {
      callbacks.setHoveredEntity(null);
    }

    // Pan mode: mouse drag pans viewport
    if (state.isPanMode && state.isMouseDown) {
      if (state.panTouchStart) {
        const dx = mouseX - state.panTouchStart.x;
        const dy = mouseY - state.panTouchStart.y;
        const currentOffset = state.camera.getOffset();
        state.camera.setOffset(currentOffset.x + dx, currentOffset.y + dy);
        callbacks.markTransforming();
      }
      callbacks.setPanTouchStart({ x: mouseX, y: mouseY });
      return;
    }

    // Paint while dragging. Five of the seven brush tools do, and three of those
    // five cost the treasury money per cell — a drag is a bill. The interchange is
    // not one of them: see `DRAG_TOOLS`.
    if (isDragTool(state.currentTool) && state.isMouseDown && hoveredCell) {
      callbacks.applyBrush(hoveredCell.x, hoveredCell.y);
    }
  }

  handleMouseDown(e: MouseEvent, state: ParkingtonState, callbacks: InputCallbacks): void {
    if (state.inputBlocked) return;

    // Check if clicking selection pane close button
    if (state.selectionPaneCloseArea && isPointInRect(state.mouseX, state.mouseY, state.selectionPaneCloseArea)) {
      callbacks.setSelectedEntity(null);
      return;
    }

    // Check if clicking on a save slot
    if (state.showSaveSlots) {
      // Save slot areas are managed by the canvas UI renderer
      // The orchestrator will handle this via its own save slot areas
      return;
    }

    // Pan mode - start pan drag
    if (state.isPanMode) {
      callbacks.setIsMouseDown(true);
      callbacks.setPanTouchStart({ x: state.mouseX, y: state.mouseY });
      return;
    }

    // Handle click based on current tool
    if (state.currentTool === ParkingtonTool.SELECT) {
      if (state.hoveredEntity) {
        if (state.hoveredEntity.type === 'portal') {
          callbacks.emitColonySelectRequest(state.hoveredEntity.id);
        } else {
          callbacks.setSelectedEntity({ ...state.hoveredEntity });
        }
      } else if (state.hoveredCell) {
        // Check for portal at cell
        const portalIndex = getPortalAtCell(
          state.hoveredCell.x, state.hoveredCell.y, state.portalCells, state.sealedPortals
        );
        if (portalIndex !== null) {
          callbacks.emitColonySelectRequest(portalIndex);
        } else {
          // Check for building at cell
          const house = getHouseAtCell(state.hoveredCell.x, state.hoveredCell.y, state.houses);
          if (house) {
            callbacks.setSelectedEntity({ type: 'building', id: house.id });
          } else {
            callbacks.setSelectedEntity(null);
          }
        }
      }
      return;
    }

    // Any painting tool - apply brush
    if (isBrushTool(state.currentTool)) {
      callbacks.setIsMouseDown(true);
      if (state.hoveredCell) {
        callbacks.applyBrush(state.hoveredCell.x, state.hoveredCell.y);
      }
    }
  }

  handleMouseUp(e: MouseEvent, state: ParkingtonState, callbacks: InputCallbacks): void {
    if (state.inputBlocked) return;
    callbacks.setIsMouseDown(false);
    callbacks.setPanTouchStart(null);
  }
}

/**
 * Get the portal/colony index at a specific grid cell.
 *
 * A sealed entrance is not one. Clicking a portal jumps the camera into the
 * colony under it, and a slab is not a way in — the click lands on the ground,
 * like any other click on a facility's foundation.
 */
function getPortalAtCell(
  gridX: number,
  gridY: number,
  portalCells: ReadonlyArray<{ x: number; y: number }>,
  sealedPortals: ReadonlyArray<boolean>
): number | null {
  for (let i = 0; i < portalCells.length; i++) {
    const portal = portalCells[i];
    if (portal.x === gridX && portal.y === gridY) {
      return sealedPortals[i] ? null : i;
    }
  }
  return null;
}

/**
 * Get a house at a specific grid cell.
 */
function getHouseAtCell(gridX: number, gridY: number, houses: ParkingtonState['houses']): House | null {
  const housesWithDepth = houses.getHousesWithDepth();
  for (const { house } of housesWithDepth) {
    for (const cell of house.footprint) {
      if (house.gridX + cell.dx === gridX && house.gridY + cell.dy === gridY) {
        return house;
      }
    }
  }
  return null;
}

/**
 * Check if a point is inside a rectangle.
 */
export function isPointInRect(x: number, y: number, rect: { x: number; y: number; w: number; h: number }): boolean {
  return x >= rect.x && x <= rect.x + rect.w && y >= rect.y && y <= rect.y + rect.h;
}
